import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import vuetify from './plugins/vuetify';
import "vuetify/styles"; // Stellt sicher, dass die Styles geladen werden
import axios from 'axios';
import { loadFonts } from './plugins/webfontloader';
import { createPinia } from "pinia";
import { useAuthStore } from "@/stores/auth"; // Pinia Auth-Store importieren
import { API_BASE_URL } from "@/config";
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
import { setupAuthInterceptor } from '@/utils/axiosSetup';
import { CacheManager } from '@/utils/cacheUtils';

// Bessere Initialisierung mit Fehlerbehandlung
async function initializeApp() {
  try {
    // Axios-Interceptor für Session-Validierung aktivieren
    setupAuthInterceptor();

    // Fonts laden
    await loadFonts();

    // App erstellen
    const app = createApp(App);
    // Pinia Store hinzufügen
    const pinia = createPinia();

    // Direktiven hinzufügen
    app.directive('checked', {
        beforeMount(el, binding) {
            el.checked = !!binding.value;
        },
        updated(el, binding) {
            if (binding.value !== binding.oldValue) {
                el.checked = !!binding.value;
            }
        },
    });

    // Axios global verfügbar machen
    app.config.globalProperties.$axios = axios;

    // Plugins & Router registrieren
    app.use(router);
    app.use(vuetify);
    app.use(pinia);
    app.use(Toast, {
        // Optionale Konfiguration
        timeout: 2500,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        showCloseButtonOnHover: true,
        pauseOnFocusLoss: false,
    });

    // Warte auf DOM-Bereitschaft
    await new Promise(resolve => {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', resolve);
      } else {
        resolve(true);
      }
    });

    // App mounten
    app.mount('#app');

    // Axios global verfügbar machen
    axios.defaults.baseURL = API_BASE_URL; // Globaler API-Endpoint setzen

    // 🔄 Authentifizierung wiederherstellen
    const authStore = useAuthStore();

    // Stelle sicher, dass es nur einmal läuft
    if (!authStore.isAuthenticated) {
      await authStore.restoreAuthentication();
    }

    // Service Worker für Cache-Management registrieren
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js')
          .then(registration => {
          })
          .catch(error => {
            console.error('Service Worker Registrierung fehlgeschlagen:', error);
          });
      });
    }

    // Nach erfolgreichem Start - Cache-Flags zurücksetzen
    CacheManager.clearReloadFlags();

  } catch (error) {
    console.error('Fehler beim Initialisieren der App:', error);

    // Fallback: Versuche Seite neu zu laden
    if (!sessionStorage.getItem('appInitRetried')) {
      sessionStorage.setItem('appInitRetried', 'true');
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      // Zeige Fehlermeldung wenn Retry auch fehlschlägt
      document.getElementById('app')!.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; background: linear-gradient(135deg, rgba(15, 15, 20, 0.95) 0%, rgba(20, 20, 35, 0.95) 100%); color: white; font-family: 'PT Serif', serif; text-align: center;">
          <div>
            <h2>Fehler beim Laden der Anwendung</h2>
            <p>Bitte laden Sie die Seite manuell neu (Ctrl+F5)</p>
            <button onclick="window.location.reload()" style="padding: 10px 20px; margin-top: 20px; background: #4CAF50; color: white; border: none; border-radius: 5px; cursor: pointer;">
              Seite neu laden
            </button>
          </div>
        </div>
      `;
    }
  }
}

// Cache-Problem-Detection
function detectCacheIssues() {
  // Prüfe ob CSS geladen wurde
  const stylesheets = document.styleSheets;
  let vuetifyLoaded = false;

  for (let i = 0; i < stylesheets.length; i++) {
    try {
      const href = stylesheets[i].href;
      if (href && (href.includes('vuetify') || href.includes('main'))) {
        vuetifyLoaded = true;
        break;
      }
    } catch (e) {
      // Cross-origin styles können nicht überprüft werden
    }
  }

  return vuetifyLoaded;
}

// Starte App-Initialisierung
document.addEventListener('DOMContentLoaded', () => {
  // Reset retry flag bei erfolgreichem DOM Load
  sessionStorage.removeItem('appInitRetried');

  // Kurze Verzögerung um sicherzustellen dass alle Ressourcen geladen sind
  setTimeout(() => {
    initializeApp();
  }, 100);
});

// Fallback falls DOMContentLoaded bereits gefeuert wurde
if (document.readyState !== 'loading') {
  sessionStorage.removeItem('appInitRetried');
  setTimeout(() => {
    initializeApp();
  }, 100);
}
